/**
 * Pure device-health rules shared by the right-nav Device Health panel and the
 * preflight checklist (FLY3D-1055).
 *
 * No Vue or Pinia here so the rules can be unit tested in the node
 * environment. The reactive wrapper lives in `useDeviceHealth.ts`.
 */
import { StateColors } from '@/constants/ColorStates'

/** Severity of one DJI HMS message after `getStateFromWarningLevel`. */
export type HealthLogState = 'warning' | 'error' | 'danger'

/** Rolled-up state of one module card. */
export type HealthModuleState = 'OFFLINE' | 'PERFECT' | 'POOR' | 'CRITICAL' | 'UNKNOWN'

export type HealthDeviceKind = 'drone' | 'rc' | 'dock'

/** The `Module` field DJI sends on the DEVICE_HEALTH topic, used verbatim as the record key. */
export type HealthModuleKey =
  | 'Propulsion'
  | 'Avionics'
  | 'Vision_positioning'
  | 'Battery'
  | 'Image_transmission'
  | 'Remote_controller'
  | 'Dock'

export interface DeviceHealthMessage {
  info: string
  code: string
  state: HealthLogState
  timeStamp: string
  /** `Date.now()` at ingestion. Drives age-based pruning. */
  receivedAt: number
}

export type DeviceHealthMessages = Record<string, DeviceHealthMessage[]>

export interface HealthModuleDescriptor {
  key: HealthModuleKey
  label: string
  icon: string
  deviceKind: HealthDeviceKind
}

export interface OfflineFlags {
  droneOff: boolean
  rcOff: boolean
  dockOff: boolean
}

export interface HealthModule extends HealthModuleDescriptor {
  state: HealthModuleState
  offline: boolean
  /** What the panel card prints: 'Offline' while the owning device is off, else `label`. */
  displayLabel: string
  logs: DeviceHealthMessage[]
}

export interface HealthAttentionSummary {
  poor: number
  critical: number
  offline: number
  /** True when at least one module is POOR or CRITICAL. */
  needsAttention: boolean
  text: string
}

/** How long a health message stays visible after it was received. */
export const HEALTH_MESSAGE_TTL_MS = 20_000
/** How often stale messages are pruned. */
export const HEALTH_PRUNE_TICK_MS = 1_000
export const MAX_HEALTH_MESSAGES_PER_MODULE = 5

/** The seven module cards, in the order the panel renders them. */
export const HEALTH_MODULES: readonly HealthModuleDescriptor[] = [
  { key: 'Propulsion', label: 'Propulsion', icon: 'launch', deviceKind: 'drone' },
  { key: 'Avionics', label: 'Avionics', icon: 'air_plane', deviceKind: 'drone' },
  {
    key: 'Vision_positioning',
    label: 'Vision Positioning',
    icon: 'ariel_view_sensor',
    deviceKind: 'drone',
  },
  { key: 'Battery', label: 'Battery', icon: 'battery', deviceKind: 'drone' },
  {
    key: 'Image_transmission',
    label: 'Image Transmission',
    icon: 'video_camera',
    deviceKind: 'drone',
  },
  { key: 'Remote_controller', label: 'Remote Controller', icon: 'rc_controller', deviceKind: 'rc' },
  { key: 'Dock', label: 'Dock', icon: 'drone', deviceKind: 'dock' },
]

export const isModuleOffline = (kind: HealthDeviceKind, flags: OfflineFlags): boolean => {
  switch (kind) {
    case 'drone':
      return flags.droneOff || flags.rcOff
    case 'rc':
      return flags.rcOff
    case 'dock':
      return flags.dockOff
  }
}

/** OFFLINE beats everything, then any danger log, then any error log. Warning-only is healthy. */
export const moduleStateFor = (
  logs: readonly DeviceHealthMessage[] | undefined,
  offline: boolean,
): HealthModuleState => {
  if (offline) return 'OFFLINE'
  if (!logs || logs.length === 0) return 'PERFECT'
  if (logs.some((log) => log.state === 'danger')) return 'CRITICAL'
  if (logs.some((log) => log.state === 'error')) return 'POOR'
  return 'PERFECT'
}

export const buildHealthModules = (
  messages: DeviceHealthMessages,
  flags: OfflineFlags,
): HealthModule[] =>
  HEALTH_MODULES.map((descriptor) => {
    const offline = isModuleOffline(descriptor.deviceKind, flags)
    const logs = messages[descriptor.key] ?? []
    return {
      ...descriptor,
      offline,
      state: moduleStateFor(logs, offline),
      displayLabel: offline ? 'Offline' : descriptor.label,
      logs,
    }
  })

/** Colour of the panel badge and the preflight header dot. */
export const overallHealthColor = (
  modules: readonly HealthModule[],
  flags: OfflineFlags,
): string => {
  if (flags.rcOff || flags.droneOff) return StateColors.OFFLINE
  const states = modules.map((module) => module.state)
  if (states.includes('CRITICAL')) return StateColors.ERROR
  if (states.includes('POOR')) return StateColors.WARNING
  return StateColors.SUCCESS
}

/**
 * Returns a new record holding only messages received within `maxAgeMs`.
 * Modules left empty are dropped. A message without `receivedAt` is treated as stale.
 */
export const pruneStaleHealthMessages = (
  messages: DeviceHealthMessages,
  now: number,
  maxAgeMs: number = HEALTH_MESSAGE_TTL_MS,
): DeviceHealthMessages => {
  const next: DeviceHealthMessages = {}
  for (const [key, logs] of Object.entries(messages)) {
    const fresh = logs.filter(
      (log) => typeof log.receivedAt === 'number' && now - log.receivedAt <= maxAgeMs,
    )
    if (fresh.length > 0) next[key] = fresh
  }
  return next
}

export const countHealthMessages = (messages: DeviceHealthMessages): number =>
  Object.values(messages).reduce((total, logs) => total + logs.length, 0)

export const offlineDevicesText = (flags: OfflineFlags): string => {
  const parts: string[] = []
  if (flags.rcOff) parts.push('Remote controller offline')
  else if (flags.droneOff) parts.push('Drone offline')
  if (flags.dockOff) parts.push('Dock offline')
  return parts.join(', ') || 'Offline'
}

export const healthAttentionSummary = (
  modules: readonly HealthModule[],
  flags: OfflineFlags,
): HealthAttentionSummary => {
  const poor = modules.filter((module) => module.state === 'POOR').length
  const critical = modules.filter((module) => module.state === 'CRITICAL').length
  const offline = modules.filter((module) => module.state === 'OFFLINE').length
  const attention = poor + critical
  const needsAttention = attention > 0

  let text = 'All modules healthy'
  if (needsAttention) {
    const plural = attention === 1 ? '' : 's'
    const verb = attention === 1 ? 'needs' : 'need'
    text = `${attention} module${plural} ${verb} attention`
  } else if (offline > 0) {
    text = offlineDevicesText(flags)
  }

  return { poor, critical, offline, needsAttention, text }
}

export const healthLogIcon = (state: HealthLogState | string): string => {
  if (state === 'warning') return 'caution_triangle'
  if (state === 'danger') return 'x_circle'
  return 'caution_circle'
}

export const healthLogColor = (state: HealthLogState | string): string => {
  if (state === 'warning') return StateColors.WARNING
  if (state === 'danger') return StateColors.ERROR
  return StateColors.NEUTRAL
}

export const moduleStateLabel = (state: HealthModuleState): string => {
  switch (state) {
    case 'PERFECT':
      return 'Healthy'
    case 'POOR':
      return 'Attention'
    case 'CRITICAL':
      return 'Critical'
    case 'OFFLINE':
      return 'Offline'
    default:
      return 'Unknown'
  }
}

export const moduleStateColor = (state: HealthModuleState): string => {
  switch (state) {
    case 'PERFECT':
      return StateColors.SUCCESS
    case 'POOR':
      return StateColors.WARNING
    case 'CRITICAL':
      return StateColors.ERROR
    case 'OFFLINE':
      return StateColors.OFFLINE
    default:
      return StateColors.NEUTRAL
  }
}
